/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { recaptchaMessage } from '../configs';
import { ReCaptchaModel } from '../types/recaptcha.types';

const getConfigStorage = async (
  storageKey: string,
  retries = 1,
  delay = 1000
): Promise<ReCaptchaModel | null> => {
  const storedConfig = sessionStorage.getItem(storageKey);

  if (storedConfig !== null) {
    return JSON.parse(storedConfig);
  } else if (retries > 0) {
    await new Promise((resolve) => setTimeout(resolve, delay));

    return getConfigStorage(storageKey, retries - 1, delay);
  }

  return null;
};

const setConfigStorage = (
  storageKey: string,
  config: ReCaptchaModel,
  logger: boolean
) => {
  if (!storageKey || !config.websiteKey) return null;

  try {
    sessionStorage.setItem(storageKey, JSON.stringify(config));
  } catch (error) {
    logger && console.error(recaptchaMessage.failedSetStorageConfig, error);
    return null;
  }
};

export { getConfigStorage, setConfigStorage };
